/**
 * ResultScreen.tsx - Shows group results for the Attribute Framing experiment
 * Last updated: 2025-11-11 12:09:00
 * Updated by: JulianPolly, Copilot
 */

import React, { useEffect, useState, useCallback } from "react";
import { Bar, BarChart, Tooltip, XAxis, YAxis, ResponsiveContainer, LabelList, Cell } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenProps } from "../../SequenceController";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, chartColors, commonStyles } from "../../../../components/colors";

// Colors for options - using primary chart colors
const choiceColors = chartColors.primary; // [lichtBlau, darkGreen]

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 470,
    maxWidth: 650,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 250
};

const resultMessageStyle: React.CSSProperties = {
    ...commonStyles.card,
    marginTop: 32,
    textAlign: "center",
    fontSize: 17,
    background: colors.backgroundLight,
    maxWidth: 650,
    marginLeft: "auto",
    marginRight: "auto",
};

const chartBlockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    gap: 24,
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8
};

const chartStyle: React.CSSProperties = {
    minWidth: 180,
    maxWidth: 550,
    flex: 1
};

const legendStyle: React.CSSProperties = {
    fontSize: 14,
    color: colors.textMedium,
    marginTop: 15,
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    gap: 8
};

const legendItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8
};

const legendColorBoxStyle = (color: string): React.CSSProperties => ({
    width: 16,
    height: 16,
    backgroundColor: color,
    display: "inline-block",
    marginRight: 8,
    borderRadius: 4
});

// Custom Tooltip with percentages
const CustomTooltip = ({ active, payload, label, total }: any) => {
    if (active && payload && payload.length) {
        const value = payload[0].value;
        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;

        return (
            <div style={{
                background: "#fff",
                border: `1px solid ${colors.silver}`,
                padding: 12,
                borderRadius: 8,
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                <div style={{
                    fontWeight: 600,
                    marginBottom: 6,
                    color: colors.brillantBlue
                }}>
                    {label}
                </div>
                <div style={{ color: payload[0].color }}>
                    {value} ({percentage}%)
                </div>
            </div>
        );
    }
    return null;
};

export function ResultScreenComponent(props: ScreenProps) {
    const { t, language } = useLanguage();
    const { getGroupData, userData } = useSession();
    const [chartData, setChartData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [userChoiceMessage, setUserChoiceMessage] = useState("");
    const [userReason, setUserReason] = useState<string | undefined>(""); // 🟢

    // Helper function for texts and labels with translations
    const getChoiceText = useCallback((choice: number) => {
        switch (choice) {
            case 0: return t('experiments', 'attributeFraming', 'screens', 'task', 'options', 'recycled', 'title');
            case 1: return t('experiments', 'attributeFraming', 'screens', 'task', 'options', 'new', 'title');
            default: return t('experiments', 'bandwagon', 'screens', 'results', 'noChoice');
        }
    }, [t]);

    const getChoiceDescription = useCallback((choice: number) => {
        switch (choice) {
            case 0: return t('experiments', 'attributeFraming', 'screens', 'task', 'options', 'recycled', 'description');
            case 1: return t('experiments', 'attributeFraming', 'screens', 'task', 'options', 'new', 'description');
            default: return '';
        }
    }, [t]);

    // Texte basierend auf der aktuellen Sprache
    const texts = {
        de: {
            classResultsTitle: "Ergebnisse deiner Klasse - Hier siehst du, wie deine Klasse abgestimmt hat.",
            classResults: "Ergebnisse deiner Klasse",
            yourChoice: "Deine Wahl:",
            choiceRecycled: "Du hast dich für den Becher aus 35 % recyceltem Plastik entschieden.",
            choiceNew: "Du hast dich für den Becher aus 65 % Neuplastik entschieden.",
            noChoice: "Du hast keine Auswahl getroffen.",
            yourReasonLabel: "Deine Begründung:" // 🟢
        },
        en: {
            classResultsTitle: "Class results - Here you can see how your class voted.",
            classResults: "Your class results",
            yourChoice: "Your choice:",
            choiceRecycled: "You chose the cup made of 35% recycled plastic.",
            choiceNew: "You chose the cup made of 65% new plastic.",
            noChoice: "You did not make a selection.",
            yourReasonLabel: "Your explanation:" // 🟢
        }
    };

    // Aktuelle Texte basierend auf Sprache
    const currentTexts = language === 'de' ? texts.de : texts.en;

    // Eigene Auswahl und Begründung herausfinden
    useEffect(() => {
        let foundChoice: number | undefined = undefined;
        let foundReason: string | undefined = undefined;

        if (userData && userData.AttributeFraming) {
            const eigeneFrames = Object.values(userData.AttributeFraming);
            for (const result of eigeneFrames) {
                if (
                    result &&
                    typeof result === "object" &&
                    "selections" in result &&
                    Array.isArray((result as any).selections) &&
                    (result as any).selections.length > 0
                ) {
                    const selection = (result as any).selections[(result as any).selections.length - 1];
                    if (
                        typeof selection.choice === "number" &&
                        (selection.choice === 0 || selection.choice === 1)
                    ) {
                        foundChoice = selection.choice;
                        // 🟢 Find reason
                        if ((result as any).reason && typeof (result as any).reason === "string" && (result as any).reason.trim().length > 0) {
                            foundReason = (result as any).reason.trim();
                        }
                        break;
                    }
                }
            }
        }

        // Direkt den entsprechenden Text setzen basierend auf der Auswahl und Sprache
        if (foundChoice === 0) {
            setUserChoiceMessage(`<b>${currentTexts.choiceRecycled}</b>`);
        } else if (foundChoice === 1) {
            setUserChoiceMessage(`<b>${currentTexts.choiceNew}</b>`);
        } else {
            setUserChoiceMessage(`<b>${currentTexts.noChoice}</b>`);
        }

        setUserReason(foundReason); // 🟢
    }, [userData, currentTexts]);

    useEffect(() => {
        setLoading(true);
        getGroupData().then(data => {
            const optionCounts = [0, 0];
            data?.forEach((dat: any) => {
                if (dat.AttributeFraming) {
                    Object.values(dat.AttributeFraming).forEach((result: any) => {
                        if (result?.selections?.length > 0) {
                            const selection = result.selections[result.selections.length - 1];
                            if (
                                typeof selection.choice === "number" &&
                                selection.choice >= 0 &&
                                selection.choice <= 1
                            ) {
                                optionCounts[selection.choice]++;
                            }
                        }
                    });
                }
            });

            const totalParticipants = optionCounts[0] + optionCounts[1];
            setTotal(totalParticipants);

            setChartData([
                {
                    name: getChoiceText(0),
                    description: getChoiceDescription(0),
                    Anzahl: optionCounts[0] || 0,
                    color: choiceColors[0]
                },
                {
                    name: getChoiceText(1),
                    description: getChoiceDescription(1),
                    Anzahl: optionCounts[1] || 0,
                    color: choiceColors[1]
                }
            ]);

            setLoading(false);
        }).catch(() => setLoading(false));
    }, [getGroupData, getChoiceText, getChoiceDescription]);

    useEffect(() => {
        props.setCanContinue(true);
    }, [props]);

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>
            {t('experiments', 'loading')}
        </div>;
    }

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {t('experiments', 'attributeFraming', 'screens', 'results', 'title')}
            </h2>

            <p style={{
                textAlign: "center",
                fontSize: 17,
                color: colors.textMedium,
                maxWidth: 800,
                margin: "0 auto 32px"
            }}>
                {currentTexts.classResultsTitle}
            </p>

            <div style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 48,
                marginTop: 28
            }}>
                {/* Results Chart */}
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 12
                    }}>
                        {currentTexts.classResults}
                    </h3>
                    <div style={chartBlockStyle}>
                        <div style={{ ...chartStyle, margin: "0 auto" }}>
                            <div style={{
                                fontWeight: 500,
                                marginBottom: 12,
                                textAlign: "center",
                                fontSize: 16,
                                color: colors.brillantBlue
                            }}>
                                {currentTexts.yourChoice}
                            </div>
                            <ResponsiveContainer width="100%" height={270}>
                                <BarChart data={chartData}>
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 14 }}
                                        interval={0}
                                        height={50}
                                    />
                                    <YAxis allowDecimals={false} domain={[0, 'dataMax + 1']} />
                                    <Tooltip
                                        content={<CustomTooltip total={total} />}
                                        cursor={{ fill: 'rgba(200, 200, 200, 0.2)' }}
                                    />
                                    <Bar dataKey="Anzahl" barSize={50}>
                                        {chartData.map((entry) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                        <LabelList
                                            dataKey="Anzahl"
                                            position="top"
                                            fontSize={14}
                                            fill={colors.brillantBlue}
                                            fontWeight={600}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>

                            {/* Legend explaining Cup A and Cup B */}
                            <div style={legendStyle}>
                                <div style={legendItemStyle}>
                                    <span style={legendColorBoxStyle(choiceColors[0])}></span>
                                    <span><b>{getChoiceText(0)}</b>: {getChoiceDescription(0)}</span>
                                </div>
                                <div style={legendItemStyle}>
                                    <span style={legendColorBoxStyle(choiceColors[1])}></span>
                                    <span><b>{getChoiceText(1)}</b>: {getChoiceDescription(1)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* One sentence for the user under the chart - using language-specific text */}
            <div style={resultMessageStyle}>
                <div dangerouslySetInnerHTML={{ __html: userChoiceMessage }} />
                {/* 🟢 Reason below in its own block, if present */}
                {userReason && (
                    <div style={{
                        marginTop: 10,
                        fontSize: 15,
                        color: colors.textDark,
                        background: "#f8fafc",
                        borderRadius: 8,
                        padding: "10px 16px",
                        border: `1px solid ${colors.silver}`,
                    }}>
                        <div style={{
                            fontWeight: 600,
                            color: colors.brillantBlue,
                            marginBottom: 4
                        }}>
                            {currentTexts.yourReasonLabel}
                        </div>
                        <div style={{
                            fontStyle: "italic",
                            color: colors.textMedium
                        }}>
                            {userReason}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export const ResultScreen = {
    component: ResultScreenComponent,
    defaultResult: {
        selections: [],
        usedHelp: false
    },
    canContinueDefault: true
};